/**
 * Shared design tokens + curriculum-structure constants for the AI Prompting
 * skill's terminal aesthetic. Extracted verbatim from PromptLanding.jsx so
 * PromptSandbox.jsx (and anything else in this skill) can use the exact same
 * palette instead of drifting from a second, independently-maintained copy.
 */

import { BookOpen, Zap, Target, RotateCcw, Trophy } from 'lucide-react';

/* ─── TOKENS (terminal is intentionally dark-only) ─── */
export const TT = {
  bg: '#0d0d0d',
  bgSurface: '#1a1a1a',
  bgElevated: '#111111',
  border: '#2a2a2a',
  borderHover: '#3a3a3a',
  text: '#e2e8f0',
  textMuted: '#64748b',
  textDim: '#475569',
  orange: '#ea580c',
  orangeGlow: 'rgba(234,88,12,0.35)',
  amber: '#fbbf24',
  green: '#6ee7b7',
  purple: '#a78bfa',
  cyan: '#22d3ee',
  gray: '#6b7280', // Infinite Reps node — neutral, not a phase color
  fontMono: 'var(--font-mono)',
  fontDisplay: 'var(--font-skill-ai)', // Oxanium
  fontSans: 'var(--font-body)',        // DM Sans — for readable body copy
  radius: { sm: 6, md: 10, lg: 14, xl: 18 },
  trans: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
};

/* Curriculum structure — local to this skill (see PromptLanding.jsx caveat
   B). 5 phases, 13 lessons (structure-and-allocation round — was 6
   phases; Phase 3 "Professional application" deliberately introduces no
   new technique, it's the retrieval phase for Phase 1-2 material).
   `lessons` holds the REAL `lessons.id` values, not the 1-13 lesson
   NUMBER — those two now genuinely differ (see
   backend/scripts/seedAiPromptingV2.js's LESSON_MAP comment for why the
   id can't just be 1-13: Storytelling already holds ids 4-8 in this same
   shared table). Cross-reference: db id -> lesson number is L1=1, L2=2,
   L3=3, L4=11, L5=12, L6=9, L7=13, L8=14, L9=15, L10=16, L11=10, L12=17,
   L13=18.
   Colors: kept 5 of the previous 6 (orange/purple/teal/amber/green,
   positionally unchanged for phases 1-4), dropped blue (previously
   "Expert Patterns", a phase that no longer exists in this structure)
   rather than introduce a new hex for the 5th. */
export const PHASES = [
  { num: 1, label: 'Foundations',              lessons: [1, 2, 3],    color: '#ea580c' },
  { num: 2, label: 'Control patterns',         lessons: [11, 12, 9],  color: '#7c3aed' },
  { num: 3, label: 'Professional application', lessons: [13, 14],    color: '#0d9488' },
  { num: 4, label: 'Reliability',              lessons: [15, 16, 10], color: '#d97706' },
  { num: 5, label: 'Systems and voice',        lessons: [17, 18],    color: '#059669' },
];

export const PHASE_ICONS = [BookOpen, Zap, Target, RotateCcw, Trophy];

// Dev-only progression override — read at build time, off unless the env
// var is the literal string 'true'. Vite inlines import.meta.env.* at
// build time and strips whatever branch is dead, so a missing var, an
// empty string, or anything other than 'true' compiles to `false` here —
// this cannot silently leak into a production build by omission the way
// a hardcoded `true` could. Gates ONLY node-sealing on the map
// (PromptLanding.jsx's getNodeState) — never scoring, progress writes, or
// the SRS layer, all of which read real user_progress/technique_mastery
// rows exactly as they do today regardless of this flag.
export const UNLOCK_ALL_DEV = import.meta.env.VITE_UNLOCK_ALL === 'true';

// LESSON_NAMES used to live here as a second, hand-maintained set of
// lesson titles — it disagreed with curriculum.js's own titles for 11 of
// 13 lessons (documented in docs/prompt-reps-inventory.md's appendix).
// v2 makes the database the single source: see hooks/useLessons.js's
// lessonName() helper, backed by GET /api/lessons, used wherever this
// was imported.
